import { z } from 'zod';
import { normalizeAuthType } from '../../constants';

// Param schemas
export const orgCredentialIdParam = z.object({
  id: z.string().uuid(),
});

// Query schemas
export const listOrgCredentialsQuery = z.object({
  organization_id: z.string().uuid(),
  hub_id: z.string().uuid().optional(),
});

// Body schemas — accept both lowercase (canonical) and legacy capitalized values
export const createOrgCredentialBody = z.object({
  organization_id: z.string().uuid(),
  credential_display_name: z.string().min(1).max(255),
  credential_description: z.string().max(1000).nullish(),
  authentication_type: z.enum(['api_key', 'bearer', 'basic_auth']).or(
    z.enum(['API Key', 'Bearer Token', 'Basic Auth']).transform((v) => normalizeAuthType(v) as 'api_key' | 'bearer' | 'basic_auth')
  ),
  api_key: z.string().optional(),
  access_token: z.string().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  tags: z.array(z.string().uuid()).optional(),
  environment: z.enum(['preview', 'production', 'all']).optional(),
});

export const updateOrgCredentialBody = z.object({
  credential_display_name: z.string().min(1).max(255).optional(),
  credential_description: z.string().max(1000).nullish(),
  api_key: z.string().optional(),
  access_token: z.string().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  tags: z.array(z.string().uuid()).optional(),
  environment: z.enum(['preview', 'production', 'all']).optional(),
});

// Inferred types
export type CreateOrgCredentialInput = z.infer<typeof createOrgCredentialBody>;
export type UpdateOrgCredentialInput = z.infer<typeof updateOrgCredentialBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export interface OrgCredentialResponse {
  organization_credential_id: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
  credential_display_name: string;
  credential_description: string | null;
  authentication_type: 'api_key' | 'bearer' | 'basic_auth';
  tags: string[];
  environment: 'preview' | 'production' | 'all';
}

export interface ListOrgCredentialsResponse {
  data: OrgCredentialResponse[];
}

export interface GetOrgCredentialResponse {
  data: OrgCredentialResponse;
}

export interface CreateOrgCredentialResponse {
  data: OrgCredentialResponse;
}

/**
 * One rotation target the new value did not reach — a linked connection or a linked base.
 *
 * The credential itself rotated, so the request succeeded; this is the part that did not.
 * Surfaced rather than logged because a target still holding the old value keeps working
 * until the upstream revokes it and then fails closed, and nothing else names which one.
 */
export interface CredentialRotationFailure {
  target: string;
  message: string;
}

export interface UpdateOrgCredentialResponse {
  data: OrgCredentialResponse;
  /** Absent when every linked target rotated. */
  rotation_failures?: CredentialRotationFailure[];
}
